import React, { useMemo, memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import useStyle from './EmailSubjectAndBodyInputStyle';
import TemplateTextToolbar from './TemplateTextToolbar';
import TextEditor from '../../../widgets/rich-text-editor/TextEditor';
import createLinkPlugin from '../../../widgets/rich-text-editor/plugins/link/LinkPlugin';
import createTemplateTextPlugin from './template-text-plugin/TemplateTextPlugin';

// Modified by AI on 05/26/2026. Edit #1.
const EmailSubjectAndBodyInputComponent = ({
    className = '',
    disableLink = true,
    subjectClassName = '',
    bodyClassName = '',
    emailContextData = {},
    emailSubject = '',
    emailBody = '',
    emailSubjectError = false,
    emailBodyError = false,
    showSubject = true,
    showBody = true,
    onEmailSubjectChange = () => {},
    onEmailBodyChange = () => {},
    appEventForExternalSubjectChange = null,
    appEventForExternalBodyChange = null,
    appEventForCopySubject = null,
    appEventForCopyBody = null,
    copyIconSubject = null,
    copyIconBody = null,
    isFeedbackActionShown = true,
    isTemplateTextShown,
    areStylingBtnsShown = true,
    templateSelect = null,
    textTemplateValues,
    buttonTemplateValues,
}) => {
    const { t } = useTranslation();

    const classes = useStyle({
        showSubject,
        showBody,
        emailSubjectError,
        emailBodyError,
    });
    const rootClasses = clsx(className, classes.root);
    const subjectTextEditorClasses = clsx(subjectClassName, classes.subjectTextEditor);
    const bodyTextEditorClasses = clsx(bodyClassName, classes.bodyTextEditor);

    const [activeEditorData, setActiveEditorData] = useState({});

    const subjectPlugins = useMemo(() => {
        if (!showSubject) {
            return [];
        }

        const templateTextPlugin = createTemplateTextPlugin({ templateTextContextData: emailContextData }, true);

        return [templateTextPlugin];
    }, [showSubject, disableLink, emailContextData]);

    const bodyPlugins = useMemo(() => {
        const linkPlugin = createLinkPlugin({ disableLink, context: emailContextData });
        const templateTextPlugin = createTemplateTextPlugin({ templateTextContextData: emailContextData });

        return [linkPlugin, templateTextPlugin];
    }, [disableLink, emailContextData]);

    const [isHeaderFocused, setIsHeaderFocused] = useState(false);

    return (
        <div className={rootClasses}>
            { showSubject && <TextEditor
                disableFocus
                className={subjectTextEditorClasses}
                lines={2}
                lineHeight={24}
                value={emailSubject}
                placeHolder={t('CONTACT_CANDIDATE_EMAIL_FORM_SUBJECT_PLACEHOLDER')}
                onChange={onEmailSubjectChange}
                plugins={subjectPlugins}
                settings={{
                    showLabel: false,
                    showToolBar: false,
                    resizable: false,
                }}
                appEventForExternalValueChange={appEventForExternalSubjectChange}
                appEventForCopy={appEventForCopySubject}
                onFocus={(editorState, setEditorState, onChangeInternal) => {
                    setActiveEditorData({
                        editorState, setEditorState, onChangeInternal, type: 'emailSubject',
                    });
                    setIsHeaderFocused(false);
                }}
                onBlur={() => {
                    setIsHeaderFocused(true);
                }}
                copyIcon={copyIconSubject}
            /> }
            { showBody && <TextEditor
                fullHeight
                disableFocus
                lineHeight={24}
                className={bodyTextEditorClasses}
                value={emailBody}
                placeHolder={t('CONTACT_CANDIDATE_EMAIL_FORM_BODY_PLACEHOLDER')}
                onChange={onEmailBodyChange}
                plugins={bodyPlugins}
                appEventForExternalValueChange={appEventForExternalBodyChange}
                appEventForCopy={appEventForCopyBody}
                onFocus={(editorState, setEditorState, onChangeInternal) => setActiveEditorData({
                    editorState, setEditorState, onChangeInternal, type: 'emailBody',
                })}
                copyIcon={copyIconBody}
                settings={{
                    showLabel: false,
                    showToolBar: true,
                    toolBarAlignment: 'bottom',
                    renderToolBar: (toolbarProps) => <TemplateTextToolbar
                        {...toolbarProps}
                        isHeaderFocused={isHeaderFocused}
                        type={activeEditorData.type}
                        editorState={activeEditorData.editorState || toolbarProps.editorState}
                        setEditorState={activeEditorData.setEditorState || toolbarProps.setEditorState}
                        onChangeInternal={activeEditorData.onChangeInternal || toolbarProps.onChangeInternal}
                        isFeedbackActionShown={isFeedbackActionShown}
                        areStylingBtnsShown={areStylingBtnsShown}
                        templateSelect={templateSelect}
                        textTemplateValues={textTemplateValues}
                        buttonTemplateValues={buttonTemplateValues}
                        isTemplateTextShown={isTemplateTextShown}
                    />,
                    resizable: false,
                }}
            /> }
        </div>
    );
};

EmailSubjectAndBodyInputComponent.propTypes = {
    className: PropTypes.string,
    subjectClassName: PropTypes.string,
    bodyClassName: PropTypes.string,
    emailContextData: PropTypes.object,
    disableLink: PropTypes.bool,
    emailSubject: PropTypes.string,
    emailBody: PropTypes.string,
    emailSubjectError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    emailBodyError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    showSubject: PropTypes.bool,
    showBody: PropTypes.bool,
    onEmailSubjectChange: PropTypes.func,
    onEmailBodyChange: PropTypes.func,
    appEventForExternalSubjectChange: PropTypes.string,
    appEventForExternalBodyChange: PropTypes.string,
    appEventForCopySubject: PropTypes.string,
    appEventForCopyBody: PropTypes.string,
    copyIconSubject: PropTypes.node,
    copyIconBody: PropTypes.node,
    isFeedbackActionShown: PropTypes.bool,
    areStylingBtnsShown: PropTypes.bool,
    templateSelect: PropTypes.node,
};

export default memo(EmailSubjectAndBodyInputComponent);
